import { TeamlidProfiel } from '../../../../team/model/teamlid.model';
import {
  GebeurtenisSuggestie,
  ParkourGebeurtenisUpsert,
  validatedSuggestieId,
} from '../../model/gebeurtenis';
import { Component, forwardRef, ViewChild } from '@angular/core';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { ParkourBackButtonDirective } from '../../../../shared/directives/parkour-back-button.directive';
import { TranslateModule } from '@ngx-translate/core';
import { PageLayoutComponent } from '../../../../shared/components/page-layout/page-layout.component';
import { AsyncPipe } from '@angular/common';
import { GebeurtenisFormComponent } from '../../component/gebeurtenis-form/gebeurtenis-form.component';
import { ShowIfSuccesfulDirective } from '../../../../shared/directives/show-if-succesful.directive';
import { ParkourHeaderActionComponent } from '../../../../shared/components/parkour-header-action/parkour-header-action.component';
import { PageContentDirective } from '../../../../shared/directives/page-content.directive';
import { ParkourDataPage } from '../../../../shared/parkour-data-page';
import { DirtyFormCheck } from '../../../../shared/components/dirty-form-guard';
import { VerhaalService } from '../../service/verhaal.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TeamService } from '../../../../team/service/team.service';
import {
  combineLatest,
  EMPTY,
  from,
  map,
  of,
  OperatorFunction,
  shareReplay,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs';
import { FileData, FotoId, GebeurtenisDto, ParkourGebeurtenisUpsertDto } from 'parkour-web-app-dto';
import { getSecureImage } from '../../../../shared/pipes/secure-image.pipe';
import { ParkourPopupService } from '@parkour/ui';

type PageData = {
  teamleden: TeamlidProfiel[];
  gebeurtenisSuggestie?: GebeurtenisSuggestie;
};

@Component({
  standalone: true,
  templateUrl: './gebeurtenis-add.page.html',
  imports: [
    IonHeader,
    IonToolbar,
    IonButtons,
    IonBackButton,
    ParkourBackButtonDirective,
    IonTitle,
    TranslateModule,
    IonContent,
    PageLayoutComponent,
    AsyncPipe,
    GebeurtenisFormComponent,
    ParkourHeaderActionComponent,
    ShowIfSuccesfulDirective,
    forwardRef(() => PageContentDirective),
  ],
})
export class GebeurtenisAddPage extends ParkourDataPage<PageData> implements DirtyFormCheck {
  private teamleden$ = this.teamService.getMyTeamleden().pipe(shareReplay(1));
  private shareWarningPopped = false;
  @ViewChild('gebeurtenisForm') gebeurtenisForm?: GebeurtenisFormComponent;

  constructor(
    private readonly verhaalService: VerhaalService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly teamService: TeamService,
    private readonly parkourPopupService: ParkourPopupService,
  ) {
    super();
  }

  override retrieveMainData() {
    const suggestie$ = this.activatedRoute.queryParamMap.pipe(
      switchMap((params) => {
        const suggestieId = params.get('suggestieId');
        if (suggestieId) {
          return this.verhaalService.getGebeurtenisSuggestie(validatedSuggestieId(suggestieId));
        } else {
          return of(undefined);
        }
      }),
    );

    return combineLatest({
      teamleden: this.teamService.getMyTeamleden(),
      gebeurtenisSuggestie: suggestie$,
    });
  }

  getInitialState(suggestie?: GebeurtenisSuggestie): Partial<GebeurtenisDto> {
    return suggestie
      ? {
          titel: suggestie.gebeurtenisData.titel,
          omschrijving: suggestie.gebeurtenisData.omschrijving,
          startDatum: suggestie.gebeurtenisData.startDatum,
          bijlagen: suggestie.gebeurtenisData.bijlagen,
          fotoId: suggestie.gebeurtenisData.imageId,
          thema: suggestie.gebeurtenisData.thema,
        }
      : {};
  }

  protected getExistingGebeurtenisImage = (imageId: FotoId) =>
    this.mainPageData$.pipe(
      switchMap((data) => {
        if (data.success && data.value.gebeurtenisSuggestie) {
          return getSecureImage(
            this.verhaalService.getSuggestieImageUrl(
              data.value.gebeurtenisSuggestie.id,
              imageId,
              'LARGE',
            ),
          );
        } else {
          return EMPTY;
        }
      }),
    );

  getSubmitGebeurtenisOfSuggestie = (suggestie?: GebeurtenisSuggestie) => {
    return (gebeurtenisUpsert: ParkourGebeurtenisUpsert) => {
      if (suggestie) {
        gebeurtenisUpsert = {
          ...gebeurtenisUpsert,
          originSuggestie: suggestie.id,
        };
      }

      return this.teamleden$.pipe(
        switchMap((teamleden) =>
          from(this.showShareWarning(gebeurtenisUpsert, teamleden)).pipe(
            this.mapShareWarning(() => this.gebeurtenisForm?.registerGebeurtenisForm.markAsDirty()),
            takeWhile((value) => value === 'save-form'),
            switchMap(() => {
              return this.verhaalService.addGebeurtenis(gebeurtenisUpsert).pipe(
                map(() => undefined),
                tap(() => {
                  return this.router.navigate(['..'], { relativeTo: this.activatedRoute });
                }),
              );
            }),
          ),
        ),
      );
    };
  };

  isFormDirty() {
    return this.gebeurtenisForm?.registerGebeurtenisForm.dirty ?? false;
  }

  onExistingFileDownload(
    fileData: FileData,
    gebeurtenisSuggestie: GebeurtenisSuggestie | undefined,
  ) {
    if (!gebeurtenisSuggestie) {
      throw new Error('GebeurtenisSuggestie should exist when existing file is present');
    }

    this.verhaalService.downloadSuggestieBijlage(gebeurtenisSuggestie.id, fileData).subscribe();
  }

  private async showShareWarning(
    gebeurtenisUpsert: ParkourGebeurtenisUpsertDto,
    teamleden: TeamlidProfiel[],
  ) {
    if (
      teamleden.length > 0 &&
      !this.shareWarningPopped &&
      gebeurtenisUpsert.deelMode === 'SPECIFIEK' &&
      gebeurtenisUpsert.gedeeldMet.length === 0
    ) {
      return this.parkourPopupService.showPopup({
        icon: 'alert-triangle',
        title: 'Gebeurtenis met niemand gedeeld',
        description:
          'Je deelt deze gebeurtenis niet met een teamlid. Wil je het delen nog aanpassen?',
        jaText: 'Ja, delen aanpassen',
        neeText: 'Nee, opslaan',
      });
    }
    return undefined;
  }

  private mapShareWarning(
    stayOnFormCallback: () => void,
  ): OperatorFunction<unknown, 'stay-on-form' | 'save-form'> {
    return map((result) => {
      this.shareWarningPopped = true;
      if (result === 'yes') {
        document
          .querySelector('#gebeurtenis-delen-section')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        stayOnFormCallback();
        return 'stay-on-form';
      } else {
        return 'save-form';
      }
    });
  }
}
